
import { addMinutes, format, parse, setHours, setMinutes } from "date-fns";

interface Availability {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

export interface TimeSlot {
  id: string;
  startTime: Date;
  endTime: Date;
}

export const getDayName = (date: Date): string => {
  return format(date, 'EEEE').toLowerCase();
};

export const generateTimeSlots = (
  selectedDate: Date,
  duration: number,
  availability: Record<string, Availability>
): TimeSlot[] => {
  const dayName = getDayName(selectedDate);
  const dayAvailability = availability[dayName];
  
  if (!dayAvailability || !dayAvailability.enabled) {
    return [];
  }

  const slots: TimeSlot[] = [];
  const { startTime, endTime } = dayAvailability;

  // Parse the start and end times
  const start = parse(startTime, "HH:mm", selectedDate);
  const end = parse(endTime, "HH:mm", selectedDate);

  // Generate time slots
  let currentSlotStart = start;
  
  while (currentSlotStart < end) {
    const currentSlotEnd = addMinutes(currentSlotStart, duration);
    
    if (currentSlotEnd <= end) {
      slots.push({
        id: format(currentSlotStart, 'HH:mm'),
        startTime: currentSlotStart,
        endTime: currentSlotEnd
      });
    }
    
    currentSlotStart = addMinutes(currentSlotStart, 30); // 30-minute intervals
  }
  
  return slots;
};

export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
};

export const isPastDate = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
};

export const isUnavailableDay = (
  date: Date,
  availability: Record<string, Availability>
): boolean => {
  const dayName = getDayName(date);
  const dayAvailability = availability[dayName];
  return !dayAvailability || !dayAvailability.enabled;
};

export const getDisabledDates = (
  availability: Record<string, Availability>
): ((date: Date) => boolean) => {
  return (date: Date) => {
    return (
      isPastDate(date) ||
      isUnavailableDay(date, availability)
    );
  };
};
